import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

const ACTION_LABELS = {
  created: { label: 'Created', color: '#2D5C3F', bg: '#e8f5e9' },
  updated: { label: 'Updated', color: '#1565C0', bg: '#e3f2fd' },
  completed: { label: 'Completed', color: '#2D5C3F', bg: '#e8f5e9' },
  started: { label: 'Started', color: '#E65100', bg: '#fff3e0' },
  cancelled: { label: 'Cancelled', color: '#B71C1C', bg: '#ffebee' },
  rescheduled: { label: 'Rescheduled', color: '#6A1B9A', bg: '#f3e5f5' },
  reassigned: { label: 'Reassigned', color: '#00695C', bg: '#e0f2f1' },
  paid: { label: 'Paid', color: '#2D5C3F', bg: '#e8f5e9' },
  sent: { label: 'Sent', color: '#1565C0', bg: '#e3f2fd' },
  voided: { label: 'Voided', color: '#B71C1C', bg: '#ffebee' },
  report_queued: { label: 'Report Queued', color: '#E65100', bg: '#fff3e0' },
  report_released: { label: 'Report Released', color: '#2D5C3F', bg: '#e8f5e9' },
  report_rejected: { label: 'Report Rejected', color: '#B71C1C', bg: '#ffebee' },
};

const SOURCE_ICONS = { admin: '🖥️', mobile: '📱', system: '⚙️', api: '🔌' };
const ENTITY_ICONS = { work_order: '📋', invoice: '💵', report: '📄', client: '👤', facility: '🏢', user: '👥' };

export default function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    entity_type: '', performed_by: '', action: '', source: '',
    from_date: '', to_date: '', page: 1
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.set('limit', 50);
      const res = await api.get(`/activity-log?${params}`);
      const data = res.data || res;
      setLogs(data.data || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    api.get('/users').then(r => { const d = r.data || r; if (Array.isArray(d)) setUsers(d); else if (d.users) setUsers(d.users); else if (d.data) setUsers(d.data); }).catch(() => setUsers([]));
  }, []);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries({ ...filters, limit: 10000 }).forEach(([k, v]) => { if (v) params.append(k, v); });
    const res = await api.get(`/activity-log?${params}`);
    const data = res.data || res;
    const rows = [['Date', 'Entity', 'ID', 'Label', 'Action', 'Performed By', 'Source', 'Notes']];
    (data.data || []).forEach(l => rows.push([
      formatDate(l.performed_at), l.entity_type, l.entity_id,
      l.entity_label || '', l.action, l.performed_by_name || 'System',
      l.source, (l.notes || '').replace(/,/g, ';')
    ]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const actionInfo = (action) => ACTION_LABELS[action] || { label: action, color: '#555', bg: '#f5f5f5' };

  return (
    <Layout>
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a1a1a' }}>🔍 Audit Trail</h1>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
            Complete activity history — {total.toLocaleString()} events recorded
          </p>
        </div>
        <button onClick={exportCsv} style={{
          background: '#2D5C3F', color: '#fff', border: 'none',
          borderRadius: 8, padding: '10px 20px', cursor: 'pointer',
          fontWeight: 600, fontSize: 14
        }}>⬇️ Export CSV</button>
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12
      }}>
        <select value={filters.entity_type} onChange={e => setFilter('entity_type', e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
          <option value="">All Entity Types</option>
          <option value="work_order">Work Order</option>
          <option value="invoice">Invoice</option>
          <option value="report">Report</option>
          <option value="client">Client</option>
          <option value="facility">Facility</option>
          <option value="user">User</option>
        </select>

        <select value={filters.action} onChange={e => setFilter('action', e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
          <option value="">All Actions</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select value={filters.performed_by} onChange={e => setFilter('performed_by', e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
          <option value="">All Users</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
          ))}
        </select>

        <select value={filters.source} onChange={e => setFilter('source', e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
          <option value="">All Sources</option>
          <option value="admin">Admin Panel</option>
          <option value="mobile">Mobile App</option>
          <option value="system">System</option>
        </select>

        <input type="date" value={filters.from_date} onChange={e => setFilter('from_date', e.target.value)}
          placeholder="From date"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />

        <input type="date" value={filters.to_date} onChange={e => setFilter('to_date', e.target.value)}
          placeholder="To date"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />

        <button onClick={() => setFilters({ entity_type: '', performed_by: '', action: '', source: '', from_date: '', to_date: '', page: 1 })}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer', fontSize: 13 }}>
          Clear Filters
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading audit trail...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>No activity records found</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                {['Date & Time', 'Entity', 'Record', 'Action', 'Performed By', 'Source', 'Notes'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const ai = actionInfo(log.action);
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>
                      {formatDate(log.performed_at)}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                      <span>{ENTITY_ICONS[log.entity_type] || '📌'} </span>
                      <span style={{ color: '#666', textTransform: 'capitalize' }}>{log.entity_type?.replace('_', ' ')}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#1a1a1a', maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.entity_label || `#${log.entity_id}`}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                        fontSize: 12, fontWeight: 700,
                        color: ai.color, background: ai.bg
                      }}>{ai.label}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#333' }}>
                      {log.performed_by_name || <span style={{ color: '#aaa' }}>System</span>}
                      {log.performed_by_role && (
                        <div style={{ fontSize: 11, color: '#999', textTransform: 'capitalize' }}>{log.performed_by_role}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                      {SOURCE_ICONS[log.source] || '—'} {log.source}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#666', maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.notes || '—'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > 50 && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#666' }}>
              Showing {((filters.page - 1) * 50) + 1}–{Math.min(filters.page * 50, total)} of {total.toLocaleString()}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setFilter('page', filters.page - 1)} disabled={filters.page === 1}
                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', background: filters.page === 1 ? '#f5f5f5' : '#fff' }}>
                ← Prev
              </button>
              <span style={{ padding: '6px 14px', fontSize: 13, color: '#555' }}>Page {filters.page}</span>
              <button onClick={() => setFilter('page', filters.page + 1)} disabled={filters.page * 50 >= total}
                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', background: filters.page * 50 >= total ? '#f5f5f5' : '#fff' }}>
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
